import { projectsProjectReferenceSnapshotSchema } from '@crest/module-projects/contracts/project-reference';
import { z } from 'zod';
import { estimatingCurrencyCodeSchema, estimatingEstimateStatusSchema, estimatingEstimateTierSchema, estimatingMoneyCentsSchema, estimatingQuantityTenThousandthsSchema, } from './estimating-estimate.contract.js';
export const estimatingProposalContentCapability = 'estimating.proposal-content.v1';
export const estimatingProposalOptionAvailabilitySchema = z.enum([
    'selectable',
    'selected',
    'draft',
    'superseded',
    'expired',
    'declined',
    'voided',
    'locked',
]);
export const estimatingProposalSelectionSchema = z.strictObject({
    packageId: z.string().uuid(),
    estimateId: z.string().uuid(),
    proposalEstimateVersionId: z.string().uuid(),
    acceptanceVersionId: z.string().uuid(),
    tierCode: estimatingEstimateTierSchema,
    acceptanceMethod: z.enum(['customer', 'staff']),
    acceptedAt: z.string().datetime({ offset: true }),
});
export const estimatingProposalCustomerLineSchema = z.strictObject({
    lineKey: z.string().uuid(),
    lineNumber: z.number().int().positive(),
    groupLabel: z.string().trim().min(1).max(120).nullable(),
    name: z.string().trim().min(1).max(240),
    description: z.string().max(4_000).nullable(),
    quantityTenThousandths: estimatingQuantityTenThousandthsSchema,
    unit: z.string().trim().min(1).max(60),
    unitPriceCents: estimatingMoneyCentsSchema,
    lineTotalCents: estimatingMoneyCentsSchema,
    optional: z.boolean(),
});
const estimatingProposalCustomerTotalsSchema = z.strictObject({
    subtotalCents: estimatingMoneyCentsSchema,
    optionalTotalCents: estimatingMoneyCentsSchema,
    discountCents: estimatingMoneyCentsSchema,
    taxCents: estimatingMoneyCentsSchema,
    totalCents: estimatingMoneyCentsSchema,
});
export const estimatingProposalOptionSchema = z.strictObject({
    tierCode: estimatingEstimateTierSchema,
    estimateId: z.string().uuid(),
    proposalEstimateVersionId: z.string().uuid(),
    estimateNumber: z.string().trim().min(1).max(80),
    status: estimatingEstimateStatusSchema,
    availability: estimatingProposalOptionAvailabilitySchema,
    customerSelectable: z.boolean(),
    title: z.string().trim().min(1).max(240),
    customerMessage: z.string().max(8_000).nullable(),
    currencyCode: estimatingCurrencyCodeSchema,
    expiresAt: z.string().datetime({ offset: true }).nullable(),
    totals: estimatingProposalCustomerTotalsSchema,
    lines: z.array(estimatingProposalCustomerLineSchema).min(1).max(500),
}).superRefine((option, context) => {
    if (option.customerSelectable !== (option.availability === 'selectable')) {
        context.addIssue({
            code: 'custom',
            path: ['customerSelectable'],
            message: 'Only a selectable proposal option may be customer selectable.',
        });
    }
});
export const estimatingProposalContentSchema = z.strictObject({
    contractVersion: z.literal(estimatingProposalContentCapability),
    packageId: z.string().uuid(),
    packageContentHash: z.string().regex(/^[a-f0-9]{64}$/),
    presentationHash: z.string().regex(/^[a-f0-9]{64}$/),
    projectReference: projectsProjectReferenceSnapshotSchema,
    generatedAt: z.string().datetime({ offset: true }),
    selectionState: z.enum(['open', 'locked', 'unavailable']),
    selection: estimatingProposalSelectionSchema.nullable(),
    options: z.array(estimatingProposalOptionSchema).min(1).max(3),
}).superRefine((content, context) => {
    const tierOrder = ['standard', 'premium', 'complete'];
    const indexes = content.options.map(({ tierCode }) => tierOrder.indexOf(tierCode));
    if (indexes.some((entry, index) => index > 0 && entry <= indexes[index - 1])) {
        context.addIssue({
            code: 'custom',
            path: ['options'],
            message: 'Proposal options must be unique and ordered Standard, Premium, Complete.',
        });
    }
    const selectable = content.options.filter(({ availability }) => availability === 'selectable');
    const selected = content.options.filter(({ availability }) => availability === 'selected');
    if (content.selectionState === 'open' && (content.selection !== null || selectable.length === 0 || selected.length > 0)) {
        context.addIssue({ code: 'custom', path: ['selectionState'], message: 'An open proposal requires selectable options and no selection.' });
    }
    if (content.selectionState === 'locked' && (content.selection === null || selected.length !== 1 || selectable.length > 0 ||
        content.options.some(({ availability }) => !['selected', 'locked'].includes(availability)))) {
        context.addIssue({ code: 'custom', path: ['selectionState'], message: 'A locked proposal requires exactly one selected option.' });
    }
    if (content.selectionState === 'unavailable' && (content.selection !== null || selectable.length > 0 || selected.length > 0)) {
        context.addIssue({ code: 'custom', path: ['selectionState'], message: 'An unavailable proposal cannot contain a selection or selectable option.' });
    }
    if (content.selection !== null && !content.options.some((option) => option.estimateId === content.selection?.estimateId &&
        option.proposalEstimateVersionId === content.selection.proposalEstimateVersionId &&
        option.tierCode === content.selection.tierCode)) {
        context.addIssue({ code: 'custom', path: ['selection'], message: 'Proposal selection must name an exact package option.' });
    }
});
export const estimatingProposalContentResponseSchema = z.strictObject({
    data: estimatingProposalContentSchema,
});
//# sourceMappingURL=estimating-proposal-content.contract.js.map